import React, { useState, useEffect } from 'react';
import { ArrowLeft, Mail, User, Briefcase, Phone, MessageSquare, Send, Check, AlertCircle, Shield, Building2, Globe, Tag } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { customerApi } from '../services/customerApi';
import logo from '../assets/logo.gif';

// UserRole enum to match backend
enum UserRole {
  ADMIN = 'admin',
  CREATOR = 'creator',
  VIEWER = 'viewer',
  WAITLISTED = 'waitlisted'
}


interface FormData {
  fullName: string;
  email: string;
  accountType: 'personal' | 'work' | '';
  role: string;
  companyName: string;
  companyUrl: string;
  phoneNumber: string;
  projectDescription: string;
}

interface SubmissionResult {
  success: boolean;
  message?: string;
  error?: string;
  userRole?: string;
}

const RequestWaitlistForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    accountType: '',
    role: '',
    companyName: '',
    companyUrl: '',
    phoneNumber: '',
    projectDescription: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

const [emailCheckLoading, setEmailCheckLoading] = useState(false);
const [emailCheckTimeout, setEmailCheckTimeout] = useState<NodeJS.Timeout | null>(null);



// Pre-fill email from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailFromUrl = searchParams.get('email');
    
    if (emailFromUrl) {
      setFormData(prev => ({
        ...prev,
        email: emailFromUrl
      }));
      
      // Validate the pre-filled email
      if (validateEmail(emailFromUrl)) {
        validateEmailAvailability(emailFromUrl);
      }
    }
  }, [location.search]);

  useEffect(() => {
      // Cleanup timeout on component unmount
      return () => {
        if (emailCheckTimeout) {
          clearTimeout(emailCheckTimeout);
        }
      };
    }, [emailCheckTimeout]);

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateEmailAvailability = async (email: string) => {
  // Clear any existing timeout
  if (emailCheckTimeout) {
    clearTimeout(emailCheckTimeout);
  }

  // Don't check if email is empty or invalid format
  if (!email.trim() || !validateEmail(email)) {
    return;
  }

  setEmailCheckLoading(true);
  
  try {
    // Check waitlist profile only (not whitelist - that's internal backend logic)
    const profileCheck = await customerApi.checkWaitlistProfile(email.trim());
      
    // If email exists in waitlist profile 
    if (profileCheck.exists) {
      setErrors(prev => ({
        ...prev,
        email: 'This user already is part of the CR3ATOR family. Please check your email for further updates or choose a different email ID.'
      }));
    } else {
      // Clear email error if it was previously set for this reason
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.email?.includes('CR3ATE family')) {
          delete newErrors.email;
        }
        return newErrors;
      });
    }
  } catch (error) {
    console.warn('Email availability check failed:', error);
    // Don't show error to user - let them proceed and handle on submit
  } finally {
    setEmailCheckLoading(false);
  }
};

  const validateUrl = (url: string) => {
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  };

  const validatePhone = (phone: string) => {
    const re = /^[\+]?[1-9][\d]{0,15}$/;
    return re.test(phone.replace(/[\s\-\(\)]/g, ''));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (name === 'email') {
    // Clear any existing timeout
    if (emailCheckTimeout) {
      clearTimeout(emailCheckTimeout);
    }

    // Set new timeout to check email after 1 second of no typing
    const timeout = setTimeout(() => {
      validateEmailAvailability(value);
    }, 1000);

    setEmailCheckTimeout(timeout);
    }
  };

  const handleAccountTypeChange = (type: 'personal' | 'work') => {
    setFormData(prev => ({
      ...prev,
      accountType: type,
      // Clear work-related fields if switching to personal
      role: type === 'personal' ? '' : prev.role,
      companyName: type === 'personal' ? '' : prev.companyName,
      companyUrl: type === 'personal' ? '' : prev.companyUrl
    }));

    // Clear related errors
    if (type === 'personal') {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.role;
        delete newErrors.companyName;
        delete newErrors.companyUrl;
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.accountType) {
      newErrors.accountType = 'Please select personal or work use';
    }

    if (formData.accountType === 'work') {
      if (!formData.role.trim()) {
        newErrors.role = 'Role is required for work accounts';
      }
      if (!formData.companyName.trim()) {
        newErrors.companyName = 'Company name is required for work accounts';
      }
      // Company URL is now optional - only validate if provided
      if (formData.companyUrl.trim() && !validateUrl(formData.companyUrl)) {
        newErrors.companyUrl = 'Please enter a valid URL';
      }
    }

    /*remove mandatory check on phone number
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!validatePhone(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }
    */
   // Phone number is now optional - only validate if provided
    if (formData.phoneNumber.trim() && !validatePhone(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    if (!formData.projectDescription.trim()) {
      newErrors.projectDescription = 'Project description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  
  if (!validate()) return;

  setStatus('submitting');
  setSubmissionResult(null);

  try {
    // First, check if email is already on waitlist to prevent duplicates
    try {
      const checkResult = await customerApi.checkWaitlistProfile(formData.email.trim());
      if (checkResult.exists) {
        setSubmissionResult({
          success: false,
          error: 'This email is already on our waitlist. Please check your email for further instructions.'
        });
        setStatus('error');
        return;
      }
    } catch (checkError) {
      // If check fails, we'll proceed anyway and let the backend handle duplicates
      console.warn('Could not check waitlist profile existence:', checkError);
    }

    // SINGLE API CALL - Backend handles both waitlist profile AND whitelist entry atomically
    const waitlistProfileData = {
      email: formData.email.trim(),
      fullName: formData.fullName.trim(),
      studioName: formData.accountType === 'work' ? formData.companyName.trim() : 'Personal Use',
      jobTitle: formData.accountType === 'work' ? formData.role.trim() : '',
      phoneNumber: formData.phoneNumber.trim() || undefined, // Make phone number optional
      project: formData.projectDescription.trim(),
      // Additional fields to match profile model structure
      productionType: undefined, // Not collected in waitlist form yet
      softwareTools: undefined,  // Not collected in waitlist form yet
      exportFormat: undefined    // Not collected in waitlist form yet
    };

    console.log('Creating waitlist profile (backend will also add to whitelist):', waitlistProfileData);
    
    const profileResponse = await customerApi.createWaitlistProfile(waitlistProfileData);
    
    if (!profileResponse.success) {
      throw new Error(profileResponse.message || 'Failed to create waitlist profile');
    }

    // Backend automatically created both waitlist profile AND whitelist entry
    setSubmissionResult({
      success: true,
      message: 'Successfully submitted waitlist request'
    });
    setStatus('success');
    setShowSuccessModal(true);

    // Clear form
    setFormData({
      fullName: '',
      email: '',
      accountType: '',
      role: '',
      companyName: '',
      companyUrl: '',
      phoneNumber: '',
      projectDescription: ''
    });

    console.log('Waitlist submission completed successfully for:', formData.email);

  } catch (error: any) {
    console.error('Waitlist submission failed:', error);
    
    let errorMessage = 'Failed to submit waitlist request. Please try again.';
    let userRole = null;
    
    // Check for userRole in different possible locations
    if (error.response?.data?.userRole) {
      userRole = error.response.data.userRole;
    } else if (error.message) {
      // Try to extract userRole from error message if it contains JSON
      try {
        const jsonMatch = error.message.match(/\{.*\}/);
        if (jsonMatch) {
          const parsedError = JSON.parse(jsonMatch[0]);
          if (parsedError.userRole) {
            userRole = parsedError.userRole;
          }
        }
      } catch (parseError) {
        console.warn('Could not parse JSON from error message:', parseError);
      }
    }
    
    console.log('Extracted userRole:', userRole); // Debug log
    
    // Two graceful error responses based on UserRole enum
    if (userRole === UserRole.WAITLISTED) {
      errorMessage = 'We already have you on our waitlist, we will reach out to you soon with updates.';
    } else if (userRole && userRole !== UserRole.WAITLISTED) {
      // For all other roles (ADMIN, CREATOR, VIEWER)
      errorMessage = 'You are already part of the Cr3ate family, please proceed to login.';
    } else if (error.response?.data?.error) {
      // Use the backend error message if no role info
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    console.log('Final error message:', errorMessage); // Debug log
    
    setSubmissionResult({
      success: false,
      error: errorMessage,
      userRole: userRole
    });
    setStatus('error');
  }
};

  const handleRetry = () => {
    setStatus('idle');
    setSubmissionResult(null);
  };

  const handleBackToCollectiv3 = () => {
    navigate('/the-gallery');
  };


  const isFormValid = () => {
    const baseFieldsValid = formData.fullName.trim() && 
                           formData.email.trim() && 
                           formData.accountType && 
                           //formData.phoneNumber.trim() && phone number is now optional
                           formData.projectDescription.trim();
    
    if (formData.accountType === 'work') {
      return baseFieldsValid && 
             formData.role.trim() && 
             formData.companyName.trim();
             // Removed companyUrl requirement
    }
    
    return baseFieldsValid;
  };

  const isSubmitting = status === 'submitting';

  const SuccessModal = () => (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#161513] rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6 border border-[#0CB4E4]/30">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#0CB4E4]/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-[#0CB4E4]/30">
            <Check className="w-8 h-8 text-[#0CB4E4]" />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-2">You're on the Waitlist!</h2>
          <p className="text-gray-300 mb-6">
              Thanks for your interest in <span className="font-medium text-white">CR3ATE.AI</span>. You're all set.
          </p>
          <div className="bg-[#161513] border border-[#0CB4E4]/30 rounded-lg px-4 py-4 text-left text-gray-300 text-sm mb-6">
            <p className="mb-2 text-white font-medium">While You Wait:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Discover scenes created by fellow cr3ators</li>
              <li>Get inspired by how others are using 3D</li>
              <li>Come back anytime to start your first project</li>
            </ul>
          </div>
        </div>
        
        <button
          onClick={handleBackToCollectiv3}
          className="w-full px-4 py-2 bg-[#0CB4E4] text-[#161513] rounded-xl hover:bg-[#0CB4E4]/90 transition-colors font-medium shadow-md"
        >
          Gallery
        </button>
      </div>
    </div>
  );

    

  return (
    <div className="min-h-screen bg-[#161513] py-8 px-4 relative">
      {/* Logo Background */}
      <div 
        className="absolute top-0 left-0 right-0 h-[400px] bg-cover bg-center bg-no-repeat opacity-20 z-0 pointer-events-none"
        style={{ backgroundImage: `url(${logo})` }}
      ></div>
      
      <div className="max-w-2xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-bold text-3xl text-white">Join the Waitlist</h1>
          <Link to="/" className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Back
          </Link>
        </div>

        {/* Error Banner */}
        {status === 'error' && submissionResult?.error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-400 mb-1">Unable to Submit Request</h3>
                <p className="text-sm text-red-300 mb-3">{submissionResult.error}</p>
                <div className="flex gap-2">
                  {/* Show login button for all roles except WAITLISTED */}
                  {submissionResult.userRole && submissionResult.userRole !== UserRole.WAITLISTED && (
                    <Link 
                      to="/login" 
                      className="text-sm bg-[#0C5484] text-white px-3 py-1 rounded-md hover:bg-[#0CB4E4]/80 transition-colors"
                    >
                      Login Here
                    </Link>
                  )}
                  <button 
                    onClick={handleRetry} 
                    className="text-sm bg-red-900/30 text-red-300 px-3 py-1 rounded-md hover:bg-red-900/50 transition-colors border border-red-500/30"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Request Form */}
        <div className="bg-[#161513] rounded-xl shadow-lg border border-[#0CB4E4]/30 p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-2">Request Early Access</h2>
            <p className="text-sm text-gray-300">Get priority access to CR3ATE.AI's 3D reconstruction platform.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name */}
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-white mb-2">
                Full Name <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  placeholder="Your full name"
                  className={`w-full pl-10 pr-4 py-3 rounded-xl bg-[#161513] border ${
                    errors.fullName ? 'border-red-500 bg-red-900/10' : 'border-[#0CB4E4]/30'
                  } focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors disabled:bg-[#161513]/50 disabled:text-gray-400 text-white`}
                  required
                />
              </div>
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-400">{errors.fullName}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                Email Address <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  placeholder="your.email@company.com"
                  className={`w-full pl-10 pr-12 py-3 rounded-xl bg-[#161513] border ${
                    errors.email ? 'border-red-500 bg-red-900/10' : 'border-[#0CB4E4]/30'
                  } focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors disabled:bg-[#161513]/50 disabled:text-gray-400 text-white`}
                  required
                />
                {/* Loading spinner for email check */}
                {emailCheckLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-[#0CB4E4] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
              {errors.email && (
                <div className="mt-2 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-red-400">{errors.email}</p>
                  {(errors.email.includes('CR3ATE family') || errors.email.includes('login')) && (
                    <div className="mt-2 flex gap-2">
                      <Link 
                        to="/login" 
                        className="text-xs bg-[#0C5484] text-white px-3 py-1 rounded-md hover:bg-[#0CB4E4]/80 transition-colors"
                      >
                        Login Here
                      </Link>
                    </div>
                  )}
                  {errors.email.includes('contact support') && (
                    <div className="mt-2">
                      <a 
                        href="mailto:support@cr3ate.ai" 
                        className="text-xs bg-[#0C5484] text-white px-3 py-1 rounded-md hover:bg-[#0CB4E4]/80 transition-colors"
                      >
                        Contact Support
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Account Type */}
            <div>
              <label className="block text-sm font-medium text-white mb-3">
                Account Type <span className="text-red-400">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label
                  className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${
                    formData.accountType === 'personal'
                      ? 'border-[#0CB4E4] bg-[#0CB4E4]/10 text-[#0CB4E4]'
                      : 'border-[#0CB4E4]/30 hover:border-[#0CB4E4]/50 text-white'
                  } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="radio"
                    name="accountType"
                    value="personal"
                    checked={formData.accountType === 'personal'}
                    onChange={() => handleAccountTypeChange('personal')}
                    disabled={isSubmitting}
                    className="sr-only"
                  />
                  <User className={`w-5 h-5 ${
                    formData.accountType === 'personal' ? 'text-[#0CB4E4]' : 'text-gray-400'
                  }`} />
                  <span className="text-sm font-medium">Personal Use</span>
                </label>
                
                <label
                  className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${
                    formData.accountType === 'work'
                      ? 'border-[#0CB4E4] bg-[#0CB4E4]/10 text-[#0CB4E4]'
                      : 'border-[#0CB4E4]/30 hover:border-[#0CB4E4]/50 text-white'
                  } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="radio"
                    name="accountType"
                    value="work"
                    checked={formData.accountType === 'work'}
                    onChange={() => handleAccountTypeChange('work')}
                    disabled={isSubmitting}
                    className="sr-only"
                  />
                  <Briefcase className={`w-5 h-5 ${
                    formData.accountType === 'work' ? 'text-[#0CB4E4]' : 'text-gray-400'
                  }`} />
                  <span className="text-sm font-medium">Work Use</span>
                </label>
              </div>
              {errors.accountType && (
                <p className="mt-1 text-sm text-red-400">{errors.accountType}</p>
              )}
            </div>

            {/* Work-specific fields */}
            {formData.accountType === 'work' && (
              <div className="space-y-4 p-4 bg-[#161513]/50 rounded-lg border border-[#0CB4E4]/20">
                <h3 className="text-sm font-medium text-[#0CB4E4]">Work Information</h3>
                
                {/* Role */}
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-white mb-2">
                    Your Role <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      placeholder="e.g. VFX Supervisor, 3D Artist"
                      className={`w-full pl-10 pr-4 py-3 rounded-xl bg-[#161513] border ${
                        errors.role ? 'border-red-500 bg-red-900/10' : 'border-[#0CB4E4]/30'
                      } focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors disabled:bg-[#161513]/50 disabled:text-gray-400 text-white`}
                      required={formData.accountType === 'work'}
                    />
                  </div>
                  {errors.role && (
                    <p className="mt-1 text-sm text-red-400">{errors.role}</p>
                  )}
                </div>

                {/* Company Name */}
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-white mb-2">
                    Company Name <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      placeholder="Your company name"
                      className={`w-full pl-10 pr-4 py-3 rounded-xl bg-[#161513] border ${
                        errors.companyName ? 'border-red-500 bg-red-900/10' : 'border-[#0CB4E4]/30'
                      } focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors disabled:bg-[#161513]/50 disabled:text-gray-400 text-white`}
                      required={formData.accountType === 'work'}
                    />
                  </div>
                  {errors.companyName && (
                    <p className="mt-1 text-sm text-red-400">{errors.companyName}</p>
                  )}
                </div>

                {/* Company URL - Now Optional */}
                <div>
                  <label htmlFor="companyUrl" className="block text-sm font-medium text-white mb-2">
                    Company Website <span className="text-gray-400">(optional)</span>
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="url"
                      id="companyUrl"
                      name="companyUrl"
                      value={formData.companyUrl}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      placeholder="https://yourcompany.com"
                      className={`w-full pl-10 pr-4 py-3 rounded-xl bg-[#161513] border ${
                        errors.companyUrl ? 'border-red-500 bg-red-900/10' : 'border-[#0CB4E4]/30'
                      } focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors disabled:bg-[#161513]/50 disabled:text-gray-400 text-white`}
                    />
                  </div>
                  {errors.companyUrl && (
                    <p className="mt-1 text-sm text-red-400">{errors.companyUrl}</p>
                  )}
                </div>
              </div>
            )}

            {/* Phone Number */}
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-white mb-2">
                Phone Number <span className="text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  placeholder="+1 (555) 123-4567"
                  className={`w-full pl-10 pr-4 py-3 rounded-xl bg-[#161513] border ${
                    errors.phoneNumber ? 'border-red-500 bg-red-900/10' : 'border-[#0CB4E4]/30'
                  } focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors disabled:bg-[#161513]/50 disabled:text-gray-400 text-white`}
                  //required
                />
              </div>
              {errors.phoneNumber && (
                <p className="mt-1 text-sm text-red-400">{errors.phoneNumber}</p>
              )}
            </div>

            {/* Project Description */}
            <div>
              <label htmlFor="projectDescription" className="block text-sm font-medium text-white mb-2">
                Tell us about your project <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-4 w-5 h-5 text-gray-400" />
                <textarea
                  id="projectDescription"
                  name="projectDescription"
                  value={formData.projectDescription}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  placeholder="Describe your project, your experience with 3D, and how you plan to use CR3ATE.AI..."
                  rows={4}
                  className={`w-full pl-10 pr-4 py-3 rounded-xl bg-[#161513] border ${
                    errors.projectDescription ? 'border-red-500 bg-red-900/10' : 'border-[#0CB4E4]/30'
                  } focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors disabled:bg-[#161513]/50 disabled:text-gray-400 text-white resize-none`}
                  required
                />
              </div>
              {errors.projectDescription && (
                <p className="mt-1 text-sm text-red-400">{errors.projectDescription}</p>
              )}
              <p className="mt-1 text-xs text-gray-400">
                {formData.projectDescription.length}/1000 characters
              </p>
            </div>

          {/* Privacy Notice */}
<div className="mt-6 pt-4 border-t border-white/10">
  <div className="flex gap-3 items-start">
    <Shield className="w-5 h-5 text-[#0CB4E4] flex-shrink-0 mt-0.5" />
    <div className="text-sm text-gray-300">
      <p className="font-medium text-white mb-2">Your information is secure</p>
      <p>
        We keep all your information private and secure. Your details will only be used to 
        evaluate your waitlist application and communicate with you about CR3ATE.AI. 
        We never share your information with third parties.
      </p>
    </div>
  </div>
</div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={!isFormValid() || isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/80 text-[#161513] font-medium rounded-xl hover:from-[#0CB4E4]/90 hover:to-[#0CB4E4]/70 focus:ring-2 focus:ring-[#0CB4E4] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-[#161513] border-t-transparent rounded-full animate-spin" />
                    Submitting Request...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Request to Join Waitlist
                  </>
                )}
              </button>
            </div>

            {/* Additional Links 
            <div className="pt-4 border-t border-[#0CB4E4]/20 text-center">
              <p className="text-sm text-gray-400">
                Already have access?{' '}
                <Link to="/login" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80 font-medium">
                  Sign in here
                </Link>
              </p>
            </div>
            */}
          </form>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && <SuccessModal />}
    </div>
  );
};

export default RequestWaitlistForm;
